import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { Star, MessageCircle } from "lucide-react";
import { useLang, WHATSAPP_URL } from "@/lib/i18n";
import { scrollToHash } from "./Nav";

const HERO_IMG = "https://a0.muscache.com/im/pictures/0197437c-38c3-4be5-b3e1-5759c1ba319f.jpg?im_w=1920";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yContent = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), { stiffness: 50, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-2.5, 2.5]), { stiffness: 50, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const EASE = [0.16, 1, 0.3, 1];

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink"
    >
      {/* cinematic image: load scale + scroll parallax + mouse tilt */}
      <motion.div style={{ y: yImg }} className="absolute inset-0">
        <motion.div
          initial={{ scale: 1.15, opacity: 0.4 }}
          animate={{ scale: 1.04, opacity: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="absolute inset-0"
        >
          <motion.img
            src={HERO_IMG}
            alt="Private Oasis Villa al tramonto"
            style={{ rotateX: rX, rotateY: rY, transformPerspective: 1200 }}
            className="h-[110%] w-full object-cover will-change-transform"
          />
        </motion.div>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-ink/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/50 via-transparent to-transparent" />

      <motion.div
        style={{ y: yContent, opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-36 md:px-10 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="mb-5 text-[11px] font-semibold uppercase tracking-[0.4em] text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)] md:text-xs"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-serif font-light leading-[0.95] text-sand drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, delay: 0.5 + i * 0.14, ease: EASE }}
                className={`block ${i === 2 ? "italic text-terra" : ""} text-[13.5vw] md:text-[7.2vw]`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-6 max-w-xl text-base font-medium leading-relaxed text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)] md:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contatti"
            onClick={(e) => {
              e.preventDefault();
              scrollToHash("#contatti");
            }}
            data-testid="hero-cta-book"
            className="group relative overflow-hidden rounded-full bg-terra px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-sand transition-shadow duration-300 hover:shadow-xl hover:shadow-terra/25"
          >
            <span className="absolute inset-0 -translate-x-full bg-ink/40 transition-transform duration-500 group-hover:translate-x-0" />
            <span className="relative">{t.hero.cta1}</span>
          </a>
          <a
            href={`${WHATSAPP_URL}?text=${encodeURIComponent(t.booking.whatsappMsg)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="flex items-center gap-2.5 rounded-full border border-sand/60 bg-ink/40 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white backdrop-blur-md transition-all duration-300 hover:border-sand hover:bg-sand hover:text-ink"
          >
            <MessageCircle className="h-4 w-4" />
            {t.hero.cta2}
          </a>
          <span
            data-testid="hero-rating-badge"
            className="flex items-center gap-2 rounded-full border border-sand/50 bg-ink/50 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-md"
          >
            <span className="flex text-terra">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            {t.hero.rating} · {t.hero.reviews}
          </span>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 md:flex md:right-10">
        <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/90">{t.hero.scroll}</span>
        <span className="scroll-line block h-12 w-px bg-white/90" />
      </div>
    </section>
  );
}
